import { useAuth } from '../contexts/AuthContext';
import { subject } from '@casl/ability';
import { abilityDefiner, Actions, AppAbility, createAppAbility, Resources } from '../shared/abilities';
import { createContext, ReactNode, useContext, useMemo } from 'react';
import { Organization } from 'src/shared/types';
import { useUrlParams } from '../utils/url';

export type AbilityContextType = {
  abilities: AppAbility;
  currentOrganization: Organization | null;
};

export const AbilityContext = createContext<AbilityContextType | null>(null);

export const AbilityProvider = ({ children }: { children: ReactNode }) => {
  const { organizations, user } = useAuth();
  const params = useUrlParams();
  const org_id = params.org_id;
  const currentOrganization = org_id ? organizations?.find((org: Organization) => org.id === Number(org_id)) as Organization || null : null;

  const abilities = useMemo(() => {
    if (!currentOrganization) { return null as unknown as AppAbility; }
    const abilities = abilityDefiner([{ org_id: currentOrganization.id, user_role: currentOrganization.user_role }], user);
    return createAppAbility(abilities.rules);
  }, [ currentOrganization, user ]);

  return (
    <AbilityContext.Provider value={{ abilities, currentOrganization }}>
      {children}
    </AbilityContext.Provider>
  );
};

const useCan = () => {
  const { abilities, currentOrganization } = useContext(AbilityContext) as AbilityContextType;
  return {
    can: (action: string, resource: string, field?: any) => {
      const s = subject(resource, field);
      return abilities?.can?.(action as Actions, s) || false;
    },
    currentOrganization
  };
};

export const useGetCurrentOrg = (): Organization | null => {
  const { can, currentOrganization } = useCan();
  if (currentOrganization && can?.(Actions.READ, Resources.ORGANIZATION, { organization_id: currentOrganization.id })) {
    return currentOrganization;
  }
  return null;
};

export const useCanManageCurrentOrg = (): boolean => {
  const { can, currentOrganization } = useCan();
  if (!currentOrganization) {
    return false;
  }
  return can?.(Actions.MANAGE, Resources.ORGANIZATION, { organization_id: currentOrganization.id }) || false;
};

export const useGetCurrentPatientId = (): string | null => {
  const params = useUrlParams();
  const patient_id = params.id;
  const { currentOrganization } = useCan();

  if (!patient_id || !currentOrganization) {
    return null;
  }

  return patient_id;
};

export const useCanReadCurrentPatient = (): boolean => {
  const { can, currentOrganization } = useCan();
  const patientId = useGetCurrentPatientId();
  if (!currentOrganization || !patientId) {
    return false;
  }
  return can?.(Actions.READ, Resources.PATIENT, { organization_id: currentOrganization.id, patient_id: patientId }) || false;
};